import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslations } from 'use-intl'
import { PricingCard } from '~/features/billing/components/PricingCard'
import { TopupCard } from '~/features/billing/components/TopupCard'
import { useCheckout } from '~/features/billing/hooks/use-checkout'
import { useSession } from '~/hooks/use-session'
import { seo } from '~/utils/seo'
import { useAuthModalStore } from '~/stores/use-auth-modal-store'

export const Route = createFileRoute('/{-$locale}/_public/pricing')({
  component: PricingPage,
  head: () => {
    return seo({
      title: 'Pricing - TabeTabi',
      description: 'Choose the perfect plan for your Japan food journey.',
    })
  },
})

function PricingPage() {
  const { data: session } = useSession()
  const { mutate: checkout, isPending } = useCheckout()
  const [loadingProduct, setLoadingProduct] = React.useState<string | null>(null)
  const { openAuthModal } = useAuthModalStore()
  const t = useTranslations('pricing')

  const handleSubscribe = (productId: string) => {
    if (!session?.session) {
      // Require login
      openAuthModal()
      return
    }

    setLoadingProduct(productId)
    checkout(
      { productId, returnUrl: window.location.href },
      {
        onSettled: () => setLoadingProduct(null),
      }
    )
  }

  const env = import.meta.env

  return (
    <div className="container mx-auto px-4 py-16 max-w-6xl">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-6">{t('title')}</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          {t('subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        {/* Explorer */}
        <PricingCard
          title={t('plans.explorer.title')}
          price={t('plans.explorer.price')}
          duration={t('plans.explorer.duration')}
          features={t.raw('plans.explorer.features') as string[]}
          buttonText={t('plans.explorer.buttonText')}
          processingText={t('processing')}
          onSubscribe={() => handleSubscribe(env.VITE_DODO_PRODUCT_ID_EXPLORER)}
          isLoading={loadingProduct === env.VITE_DODO_PRODUCT_ID_EXPLORER}
        />

        {/* Pro */}
        <PricingCard
          title={t('plans.pro.title')}
          price={t('plans.pro.price')}
          duration={t('plans.pro.duration')}
          features={t.raw('plans.pro.features') as string[]}
          buttonText={t('plans.pro.buttonText')}
          processingText={t('processing')}
          popularText={t('plans.pro.popularLabel')}
          isPopular
          onSubscribe={() => handleSubscribe(env.VITE_DODO_PRODUCT_ID_PRO)}
          isLoading={loadingProduct === env.VITE_DODO_PRODUCT_ID_PRO}
        />

        {/* Premium */}
        <PricingCard
          title={t('plans.premium.title')}
          price={t('plans.premium.price')}
          duration={t('plans.premium.duration')}
          features={t.raw('plans.premium.features') as string[]}
          buttonText={t('plans.premium.buttonText')}
          processingText={t('processing')}
          onSubscribe={() => handleSubscribe(env.VITE_DODO_PRODUCT_ID_PREMIUM)}
          isLoading={loadingProduct === env.VITE_DODO_PRODUCT_ID_PREMIUM}
        />
      </div>

      <div className="max-w-3xl mx-auto">
        <TopupCard
          title={t('topup.title')}
          description={t('topup.description', { credits: 200 })}
          price={t('topup.price')}
          oneTimePurchaseText={t('topup.oneTimePurchase')}
          buttonText={t('topup.buttonText')}
          processingText={t('processing')}
          onSubscribe={() => handleSubscribe(env.VITE_DODO_PRODUCT_ID_TOPUP)}
          isLoading={loadingProduct === env.VITE_DODO_PRODUCT_ID_TOPUP}
        />
      </div>
    </div>
  )
}
